import { useEffect, useRef } from 'react'
import Flashbar from '@cloudscape-design/components/flashbar'
import Button from '@cloudscape-design/components/button'
import type { FlashbarProps } from '@cloudscape-design/components/flashbar'
import { useNavigate } from 'react-router'
import { useNotificationStore } from '@/state/notificationStore'
import { useGenerateStore } from '@/state/generateStore'
import type { AgentStatus, NodeName } from '@/lib/types'
import { humanizeNodeName } from '@/utils/format'

const PIPELINE_ORDER: NodeName[] = [
  'resolve_client', 'persona_builder', 'strategy_planner',
  'campaign_writer', 'tone_checker', 'ethics_guardrail', 'output_formatter',
]

export default function NotificationBar() {
  const items = useNotificationStore((s) => s.items)
  const dismiss = useNotificationStore((s) => s.dismiss)
  const addNotification = useNotificationStore((s) => s.add)

  const agentStatus = useGenerateStore((s) => s.agentStatus)
  const clientId = useGenerateStore((s) => s.clientId)
  const nodeStatuses = useGenerateStore((s) => s.nodeStatuses)

  const navigate = useNavigate()
  const prevStatusRef = useRef<AgentStatus>(agentStatus)

  // Fire a one-time toast when a running campaign finishes or fails
  useEffect(() => {
    const prev = prevStatusRef.current
    prevStatusRef.current = agentStatus
    if (prev === 'running' && agentStatus === 'complete') {
      addNotification({ id: 'campaign-complete', type: 'success', content: 'Campaign completed successfully.', dismissible: true })
    } else if (prev === 'running' && agentStatus === 'error') {
      addNotification({ id: 'campaign-error', type: 'error', content: 'Campaign failed. Please try again.', dismissible: true })
    }
  }, [agentStatus, addNotification])

  const activeNode = (agentStatus === 'running' || agentStatus === 'publishing')
    ? (PIPELINE_ORDER.find(n => nodeStatuses[n] !== 'done') ?? null)
    : null

  const campaignItem: FlashbarProps.MessageDefinition | null =
    agentStatus === 'running' || agentStatus === 'publishing'
      ? {
          id: 'campaign-running',
          type: 'in-progress',
          loading: true,
          dismissible: false,
          content: activeNode ? `Campaign running — ${humanizeNodeName(activeNode)}` : 'Campaign running…',
          action: clientId
            ? (
                <Button
                  variant="inline-link"
                  onClick={() => { navigate(`/clients/${clientId}?tab=content`) }}
                >
                  View campaign
                </Button>
              )
            : undefined,
        }
      : null

  const flashbarItems: FlashbarProps['items'] = [
    ...(campaignItem ? [campaignItem] : []),
    ...items.map((item) => ({
      id: item.id,
      type: item.type,
      content: item.content,
      dismissible: item.dismissible ?? true,
      onDismiss: () => dismiss(item.id),
    })),
  ]

  return <Flashbar items={flashbarItems} />
}
